import { memo, useCallback, useMemo, useState } from "react";
import { diffLines } from "diff";

import { ArrowRight } from "components/icons/generated";
import Icon from "ds/components/Icon";

import { prettifyJson, tokenizeJsonLines } from "../helpers";
import RunCodeChangesExpandableHeader from "../ExpandableHeader";
import RunCodeChangesPrefix from "../Prefix";
import type { RunCodeChangesTokenizedDiffLine } from "../types";
import RunCodeChangesJsonDiffContent from "./DiffContent";
import styles from "./styles.module.css";

type RunCodeChangesJsonRowProps = {
  name: string;
  prefix: string;
  color: string | undefined;
  beforeJson: string | undefined;
  afterJson: string | undefined;
  afterUnknown?: boolean;
};

const RunCodeChangesJsonRow = memo(
  ({ name, prefix, color, beforeJson, afterJson, afterUnknown }: RunCodeChangesJsonRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleToggle = useCallback(() => {
      setIsExpanded((prev) => !prev);
    }, []);

    const diffResult = useMemo((): RunCodeChangesTokenizedDiffLine[] | undefined => {
      if (!isExpanded) return undefined;

      const prettyBefore = beforeJson ? prettifyJson(beforeJson) : "";
      const prettyAfter = afterJson ? prettifyJson(afterJson) : "";

      // When the after value is "known after apply" there is no concrete after to diff against —
      // afterJson is null, so prettyAfter is empty and every before line renders as removed. The
      // "(known after apply)" header marker distinguishes this from an actual deletion.
      if (!prettyBefore) return tokenizeJsonLines(prettyAfter, "added");
      if (!prettyAfter) return tokenizeJsonLines(prettyBefore, "removed");

      return diffLines(prettyBefore, prettyAfter).flatMap((change) => {
        let lineType: RunCodeChangesTokenizedDiffLine["type"] = "unchanged";

        if (change.added) lineType = "added";
        if (change.removed) lineType = "removed";

        return tokenizeJsonLines(change.value.replace(/\n$/, ""), lineType);
      });
    }, [isExpanded, beforeJson, afterJson]);

    return (
      <>
        <RunCodeChangesExpandableHeader isExpanded={isExpanded} onToggle={handleToggle}>
          <RunCodeChangesPrefix prefix={prefix} color={color} />
          <span className={styles.name}>{name}:</span>
          <span className={styles.label}>json</span>
          {afterUnknown && (
            <>
              <Icon src={ArrowRight} className={styles.arrow} />
              <span className={styles.knownAfterApply}>(known after apply)</span>
            </>
          )}
        </RunCodeChangesExpandableHeader>
        {isExpanded && diffResult && <RunCodeChangesJsonDiffContent lines={diffResult} />}
      </>
    );
  }
);

RunCodeChangesJsonRow.displayName = "RunCodeChangesJsonRow";

export default RunCodeChangesJsonRow;
